import React from 'react';
import PropTypes from 'prop-types';
import round from '../../../helpers/round';
import kennyGraph from '../../../assets/calculations/piPhi/kenny1959.JPG';

const GRAPH_WIDTH = 380;
const IMAGE = { width: 954, height: 494 };
const PLOT = {
  left: 52,
  right: 904,
  top: 28,
  bottom: 455,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const mapPiToX = (pi) => {
  const minLog = Math.log10(5);
  const maxLog = Math.log10(200);
  const normalized = (clamp(Math.log10(pi), minLog, maxLog) - minLog) / (maxLog - minLog);
  return PLOT.left + normalized * (PLOT.right - PLOT.left);
};

const mapSinPhiToY = (sinPhi) => {
  const normalized = clamp(sinPhi, 0, 1);
  return PLOT.bottom - normalized * (PLOT.bottom - PLOT.top);
};

const buildGraph = (pi, sinPhi) => {
  const height = (GRAPH_WIDTH * IMAGE.height) / IMAGE.width;
  const scale = GRAPH_WIDTH / IMAGE.width;
  const x = mapPiToX(pi) * scale;
  const y = mapSinPhiToY(sinPhi) * scale;
  const left = PLOT.left * scale;
  const bottom = PLOT.bottom * scale;

  return {
    width: GRAPH_WIDTH,
    height,
    x,
    y,
    left,
    bottom,
    verticalBottomY: bottom - 4,
    labelX: left + 4,
    labelY: y - 6,
  };
};

const ClayKennyGraphSvg = ({ pi, sinPhi, showOverlay }) => {
  const piValue = Number(pi);
  const sinPhiValue = Number(sinPhi);
  const graph = buildGraph(piValue, sinPhiValue);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${graph.width} ${graph.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Kenny PI versus sine of effective friction angle graph"
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: `${graph.width} / ${graph.height}` }}
    >
      <image href={kennyGraph} x="0" y="0" width={graph.width} height={graph.height} />
      {showOverlay && (
        <g stroke="#b33a3a" fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke">
          <line x1={graph.x} y1={graph.y} x2={graph.x} y2={graph.verticalBottomY} />
          <polyline points={`${graph.x - 3},${graph.y + 5} ${graph.x},${graph.y} ${graph.x + 3},${graph.y + 5}`} />
          <line x1={graph.left} y1={graph.y} x2={graph.x} y2={graph.y} />
          <polyline
            points={`${graph.left + 6},${graph.y - 3} ${graph.left + 1},${graph.y} ${graph.left + 6},${graph.y + 3}`}
          />
        </g>
      )}
      {showOverlay && (
        <text x={graph.labelX} y={graph.labelY} fill="#b33a3a" fontSize="10" fontWeight="700">
          sin(φ&apos;)={round(sinPhiValue, 3)}
        </text>
      )}
    </svg>
  );
};

ClayKennyGraphSvg.propTypes = {
  pi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sinPhi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showOverlay: PropTypes.bool,
};

ClayKennyGraphSvg.defaultProps = {
  showOverlay: false,
};

export default React.memo(ClayKennyGraphSvg);
